<template>
	<v-col cols="12">
		<v-row no-gutters>
			<v-col cols="12">
				<v-row no-gutters justify="center">
					<v-col cols="3" align-self="center">
						<slot name="logo"></slot>
						<slot name="left-logo"></slot>
					</v-col>
					<v-slide-x-transition duration="200" mode="out-in">
						<v-col cols="4" v-if="currentNumber">
							<roulette-spinner
								ref="rouletteSpinner"
								:key="currentNumber.playType.id"
								:reveal-array="revealArray"
								:manifest="manifest"
								:settings="settings"
								@ready="onReady"
								@finish="onFinish"
							/>
							<v-row justify="center" no-gutters>
								<v-col id="pick-img" cols="4" class="d-flex">
									<v-img
										v-if="currentNumber"
										contain
										height="100"
										class="align-self-center"
										id="currentNumberPlayType"
										:src="imageForPlayType(currentNumber.playType)"
										alt="currentNumberPlayType"
									/>
								</v-col>
							</v-row>
						</v-col>
					</v-slide-x-transition>
					<v-col cols="3" align-self="center">
						<slot name="logo"></slot>
						<slot name="right-logo"></slot>
					</v-col>
				</v-row>
			</v-col>
		</v-row>
	</v-col>
</template>

<script>
import Vue from 'vue';
import { Howl } from 'howler';
import ResultField from '@/enumerations/ResultField.json';

import greetingsSrc from 'assets/static/sounds/greetings.mp3';
import pickTwoSrc from 'assets/static/sounds/pick_two.mp3';
import pickThreeSrc from 'assets/static/sounds/pick_three.mp3';
import pickFourSrc from 'assets/static/sounds/pick_four.mp3';
import pickFiveSrc from 'assets/static/sounds/pick_five.mp3';
import zeroSrc from 'assets/static/sounds/zero.mp3';
import oneSrc from 'assets/static/sounds/one.mp3';
import twoSrc from 'assets/static/sounds/two.mp3';
import threeSrc from 'assets/static/sounds/three.mp3';
import fourSrc from 'assets/static/sounds/four.mp3';
import fiveSrc from 'assets/static/sounds/five.mp3';
import sixSrc from 'assets/static/sounds/six.mp3';
import sevenSrc from 'assets/static/sounds/seven.mp3';
import eightSrc from 'assets/static/sounds/eigth.mp3';
import nineSrc from 'assets/static/sounds/nine.mp3';
import sortitionFinishedSrc from 'assets/static/sounds/sortition_finished.mp3';
import RouletteSpinner from './RouletteSpinner.vue';

function soundFor(src) {
	return new Howl({
		src,
		volume: 0.5,
		loop: false,
		autoplay: false
	});
}

export default Vue.extend({
	name: 'ResultRevealer',
	components: {
		RouletteSpinner
	},
	props: {
		result: {
			type: Object,
			required: true
		},
		resources: {
			type: Object,
			required: true
		},
		manifest: {
			type: Array,
			required: false
		},
		settings: {
			type: Object,
			required: false,
			default: () => ({
				totalBall: 15,
				spinStartSpeed: 5,
				spinEndSpeed: 5,
				spinSpeed: 10,
				revealTimer: 5,
				rotateBalls: false,
				padNumbers: false,
				maxNumber: 10
			})
		}
	},
	data() {
		return {
			itemsForProgressBar: [],
			currentNumber: null,
			step: 0,
			greetingsSound: soundFor(greetingsSrc),
			sounds: [
				soundFor(zeroSrc),
				soundFor(oneSrc),
				soundFor(twoSrc),
				soundFor(threeSrc),
				soundFor(fourSrc),
				soundFor(fiveSrc),
				soundFor(sixSrc),
				soundFor(sevenSrc),
				soundFor(eightSrc),
				soundFor(nineSrc)
			],
			sortitionFinished: soundFor(sortitionFinishedSrc),
			picks: {
				[ResultField.PickTwo]: soundFor(pickTwoSrc),
				[ResultField.CashThree]: soundFor(pickThreeSrc),
				[ResultField.PickFour]: soundFor(pickFourSrc),
				[ResultField.PickFive]: soundFor(pickFiveSrc)
			}
		};
	},
	async created() {
		this.itemsForProgressBar = this.result.numbers.map(({ playType }) => {
			return {
				value: playType.id,
				text: playType.name,
				image: this.imageForPlayType(playType)
			};
		});
		this.greetingsSound.play();
		await new Promise((resolve) => {
			setTimeout(resolve, 4500);
		});
		this.currentNumber = this.result.numbers[0];
	},
	methods: {
		async onReady() {
			this.picks[this.currentNumber.playType.id].play();
			await new Promise((resolve) => {
				setTimeout(resolve, 2000);
			});
			this.$refs.rouletteSpinner.startSpin();
		},
		async onFinish() {
			for (const d of this.currentNumber.digits.split('')) {
				await new Promise((resolve) => {
					this.sounds[parseInt(d, 10)].play();
					setTimeout(resolve, 2000);
				});
			}
			const currentNumberIndex = this.result.numbers.findIndex(
					(x) => this.currentNumber === x
				),
				nextAvailableNumber = this.result.numbers[currentNumberIndex + 1];
			if (nextAvailableNumber) {
				this.currentNumber = nextAvailableNumber;
			} else {
				this.$emit('finish');
			}
		},
		async start() {
			for (const number of this.result.numbers) {
				this.currentNumber = number;
				this.step = this.step + 1;
				await this.$nextTick();
				await new Promise((resolve) => {
					const waitForSlotMachineTransition = 2000;
					setTimeout(resolve, waitForSlotMachineTransition);
				});
				await new Promise((resolve) => {
					setTimeout(resolve, 1500);
				});
				await this.$refs.slotsMachine.start();
				await new Promise((resolve) => {
					const timeoutToStopRoulette = 1000;
					setTimeout(resolve, timeoutToStopRoulette);
				});
				await this.$refs.slotsMachine.stop();
				await new Promise((resolve) => {
					const waitForSlotMachineTransition = 1500;
					setTimeout(resolve, waitForSlotMachineTransition);
				});
				await new Promise((resolve) => {
					const timeoutToShowNextRoulette = 2000;
					setTimeout(resolve, timeoutToShowNextRoulette);
				});
			}
			this.$emit('finish');
		},
		imageForPlayType(playType) {
			switch (playType.id) {
				case ResultField.PickTwo:
					return this.resources.pick2Img;
				case ResultField.CashThree:
					return this.resources.pick3Img;
				case ResultField.PickFour:
					return this.resources.pick4Img;
				case ResultField.PickFive:
					return this.resources.pick5Img;
			}
		}
	},
	computed: {
		revealArray() {
			if (this.currentNumber) {
				return this.currentNumber.digits.split('').map((x) => parseInt(x, 10));
			}
			return [];
		}
	}
});
</script>

<style>
</style>
